import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Loader2, Bell, Mail, MessageSquare, Smartphone, AlertCircle } from 'lucide-react';
import { formatDate } from '../utils/timeFormat';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * SendShiftReminderModal — admin clicks "Send Shift Reminder" button on
 * roster page → this modal opens.
 *
 * Props:
 *   - open, onClose
 *   - shift: { id, venue_name, shift_date, start_time }
 *   - onSent?: (result) => void
 */
const FILTER_OPTIONS = [
  { value: 'all',                label: 'All booked talent',
    desc: 'Everyone confirmed/checked-in/completed on this shift.' },
  { value: 'unconfirmed',        label: 'Unconfirmed only',
    desc: 'Booked but never tapped Confirm.' },
  { value: 'pending_onboarding', label: 'Pending onboarding',
    desc: 'Profile or Stripe onboarding incomplete.' },
  { value: 'missing_idv_dd',     label: 'Missing IDV / Direct Deposit',
    desc: "Can't be paid yet — no ID verification or no direct deposit." },
];

const CHANNEL_OPTIONS = [
  { value: 'in_app', label: 'In-app',  icon: Bell },
  { value: 'sms',    label: 'SMS',     icon: MessageSquare },
  { value: 'email',  label: 'Email',   icon: Mail },
];

const SendShiftReminderModal = ({ open, onClose, shift, onSent }) => {
  const [filterType, setFilterType] = useState('all');
  const [channels, setChannels] = useState({ in_app: true, sms: false, email: false });
  const [customMessage, setCustomMessage] = useState('');
  const [targets, setTargets] = useState(null);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchTargets = useCallback(async () => {
    if (!shift?.id) return;
    setLoadingTargets(true);
    try {
      const res = await api.get(`/admin/shifts/${shift.id}/reminder-targets`, {
        params: { filter_type: filterType },
      });
      setTargets(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load recipient list');
      setTargets(null);
    } finally {
      setLoadingTargets(false);
    }
  }, [shift?.id, filterType]);

  useEffect(() => {
    if (open) fetchTargets();
  }, [open, fetchTargets]);

  const selectedChannels = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);

  const submit = async () => {
    if (selectedChannels.length === 0) {
      toast.error('Pick at least one channel');
      return;
    }
    if (!targets || targets.count === 0) {
      toast.error('No recipients match this filter');
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/admin/shifts/${shift.id}/send-reminder`, {
        filter_type: filterType,
        channels: selectedChannels,
        custom_message: customMessage.trim() || null,
      });
      const sent = res.data.sent || {};
      const failed = res.data.failed || {};
      const totalSent = Object.values(sent).reduce((a, b) => a + b, 0);
      const totalFailed = Object.values(failed).reduce((a, b) => a + b, 0);
      toast.success(
        `Sent to ${res.data.recipients} talent · ${totalSent} delivery attempts succeeded` +
        (totalFailed ? ` · ${totalFailed} failed (see audit log)` : '')
      );
      onSent && onSent(res.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="shift-reminder-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-600" /> Send Shift Reminder
          </DialogTitle>
          <p className="text-xs text-gray-500 mt-1">
            {shift?.venue_name}
            {shift?.shift_date ? ` · ${formatDate(shift.shift_date)}` : ''}
          </p>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Filter */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-gray-500">Recipients</Label>
            <RadioGroup value={filterType} onValueChange={setFilterType} className="mt-2 space-y-2">
              {FILTER_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  htmlFor={`reminder-filter-${opt.value}`}
                  className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                    filterType === opt.value ? 'border-cyan-400 bg-cyan-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`shift-reminder-filter-${opt.value}`}
                >
                  <RadioGroupItem value={opt.value} id={`reminder-filter-${opt.value}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800">{opt.label}</div>
                    <div className="text-xs text-gray-500">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* Channels */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-gray-500">Channels</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {CHANNEL_OPTIONS.map(({ value, label, icon: Icon }) => (
                <label
                  key={value}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer text-sm ${
                    channels[value] ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid={`shift-reminder-channel-${value}`}
                >
                  <Checkbox
                    checked={channels[value]}
                    onCheckedChange={(v) => setChannels((c) => ({ ...c, [value]: !!v }))}
                  />
                  <Icon className="w-3.5 h-3.5" />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Recipient preview */}
          <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2">
            {loadingTargets ? (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading recipients…
              </div>
            ) : targets ? (
              <>
                <div className="text-xs text-gray-700">
                  <span className="font-semibold" data-testid="shift-reminder-preview-count">{targets.count}</span> talent
                  match this filter.
                </div>
                {targets.count > 0 && (
                  <div className="mt-1 text-[11px] text-gray-500 truncate">
                    {targets.targets.slice(0, 5).map((t) => t.name).join(' · ')}
                    {targets.count > 5 ? ` · +${targets.count - 5} more` : ''}
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-gray-500">—</div>
            )}
          </div>

          {/* Custom message */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-gray-500">Message (optional)</Label>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={3}
              maxLength={600}
              placeholder="Leave blank to use the default reminder for the chosen filter."
              className="mt-1 text-sm"
              data-testid="shift-reminder-custom-message"
            />
            <div className="mt-1 text-[11px] text-gray-400 text-right">{customMessage.length} / 600</div>
          </div>

          {targets && targets.count === 0 && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              <AlertCircle className="w-3.5 h-3.5" /> No talent match this filter — pick a different one or proceed once roster changes.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="shift-reminder-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={sending || loadingTargets || (targets && targets.count === 0) || selectedChannels.length === 0}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="shift-reminder-send-btn"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Sending…</>
            ) : (
              <>
                <Smartphone className="w-4 h-4 mr-1" />
                Send to {targets?.count ?? '–'} · {selectedChannels.length} channel{selectedChannels.length === 1 ? '' : 's'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendShiftReminderModal;
